import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Parcels } from '../../../interfaces/Parcels.interfaces';
import { ShipmentManagementService } from '../../../services/shipment-management.service';
import { NotificationService } from '../../../services/notification.service';
import { catchError, finalize } from 'rxjs/operators';
import { of } from 'rxjs';

@Component({
  selector: 'app-parcel-complete-popup',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, TranslateModule],
  templateUrl: './parcel-complete-popup.component.html',
  styleUrl: './parcel-complete-popup.component.css'
})
export class ParcelCompletePopupComponent implements OnInit, OnDestroy, OnChanges {
  @Input() visible: boolean = false;
  @Input() parcel: Parcels | null = null;
  @Output() closed = new EventEmitter<void>();
  @Output() completed = new EventEmitter<any>();

  parcelForm!: FormGroup;
  isSubmitting: boolean = false;
  showVolumeAlert: boolean = false;
  calculatedVolume: number = 0;

  constructor(
    private fb: FormBuilder,
    private shipmentManagementService: ShipmentManagementService,
    private notificationService: NotificationService
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    document.addEventListener('keydown', this.handleKeyDown.bind(this));
  }

  ngOnChanges(): void {
    console.log('ParcelCompletePopup - visible changed to:', this.visible);
    console.log('ParcelCompletePopup - parcel:', this.parcel);
    
    // Update form with parcel data when parcel changes
    if (this.parcel && this.parcelForm) {
      this.setFormValuesFromParcel();
    }
  }

  /**
   * Sets form values from parcel data, leaving null values as null to show only placeholders
   */
  private setFormValuesFromParcel(): void {
    if (!this.parcel) return;

    this.parcelForm.patchValue({
      parcelWeight: this.getValueOrNull(this.parcel.parcelWeight),
      parcelVolume: this.getValueOrNull(this.parcel.parcelVolume),
      parcelLength: this.getValueOrNull(this.parcel.parcelLength),
      parcelWidth: this.getValueOrNull(this.parcel.parcelWidth),
      parcelHeight: this.getValueOrNull(this.parcel.parcelHeight)
    });
  }

  /**
   * Returns the value if it's not null/undefined/0, otherwise returns null to show placeholder
   */
  private getValueOrNull(value: number | null | undefined): number | null {
    return (value !== null && value !== undefined && value !== 0) ? value : null;
  }

  /**
   * Checks if all three dimensions are set and calculates volume
   */
  private checkAndCalculateVolume(): void {
    const length = this.parcelForm.get('parcelLength')?.value;
    const width = this.parcelForm.get('parcelWidth')?.value;
    const height = this.parcelForm.get('parcelHeight')?.value;

    // Check if all dimensions are valid numbers and greater than 0
    if (length && width && height && 
        length > 0 && width > 0 && height > 0) {
      // Convert from cm³ to m³ (divide by 1,000,000)
      this.calculatedVolume = (length * width * height) / 1000000;
      this.showVolumeAlert = true;
    } else {
      this.showVolumeAlert = false;
      this.calculatedVolume = 0;
    }
  }

  /**
   * Sets the calculated volume in the volume input field
   */
  applyCalculatedVolume(): void {
    if (this.calculatedVolume > 0) {
      this.parcelForm.patchValue({
        parcelVolume: this.calculatedVolume
      });
      this.showVolumeAlert = false;
    }
  }

  /**
   * Dismisses the volume calculation alert
   */
  dismissVolumeAlert(): void {
    this.showVolumeAlert = false;
  }

  ngOnDestroy(): void {
    document.removeEventListener('keydown', this.handleKeyDown.bind(this));
  }

  private initForm(): void {
    this.parcelForm = this.fb.group({
      parcelWeight: [null, [Validators.min(0)]],
      parcelVolume: [null, [Validators.required, Validators.min(0)]],
      parcelLength: [null, [Validators.required, Validators.min(0)]],
      parcelWidth: [null, [Validators.required, Validators.min(0)]],
      parcelHeight: [null, [Validators.required, Validators.min(0)]]
    });

    // Subscribe to dimension changes to calculate volume
    this.parcelForm.get('parcelLength')?.valueChanges.subscribe(() => this.checkAndCalculateVolume());
    this.parcelForm.get('parcelWidth')?.valueChanges.subscribe(() => this.checkAndCalculateVolume());
    this.parcelForm.get('parcelHeight')?.valueChanges.subscribe(() => this.checkAndCalculateVolume());
  }

  private handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.visible) {
      this.close();
    }
  }

  close(): void {
    this.closed.emit();
    this.resetForm();
  }

  onSubmit(): void {
    if (this.parcelForm.valid && !this.isSubmitting && this.parcel) {
      this.isSubmitting = true;
      
      const formData = this.parcelForm.value;
      
      // Preparare i dati del collo con tutti i parametri necessari per il backend
      const parcelData = {
        ...this.parcel, // Include all existing parcel data
        ...formData,    // Override with form data
        parcelId: this.parcel.parcelId,
        parcelCode: this.parcel.parcelCode // Required for backend update
      };

      console.log('Final parcel data being sent to updateParcel endpoint:', {
        parcelCode: parcelData.parcelCode,
        parcelWeight: parcelData.parcelWeight,
        parcelVolume: parcelData.parcelVolume,
        parcelLength: parcelData.parcelLength,
        parcelWidth: parcelData.parcelWidth,
        parcelHeight: parcelData.parcelHeight,
        completeData: parcelData
      });

      // Chiamata all'endpoint updateParcel del backend
      this.shipmentManagementService.updateParcel(this.parcel.parcelCode!, parcelData)
        .pipe(
          catchError(error => {
            console.error('Error updating parcel:', error);
            this.notificationService.error('Errore nell\'aggiornamento del collo');
            this.isSubmitting = false;
            return of(null);
          }),
          finalize(() => {
            this.isSubmitting = false;
          })
        )
        .subscribe(response => {
          if (response) {
            console.log('Parcel updated successfully:', response);
            
            // Show success message
            this.notificationService.success('Collo aggiornato con successo');
            
            // Emit completion event with updated data for parent component
            this.completed.emit({
              success: true,
              data: parcelData,
              response: response
            });
            
            // Reset form after successful submission
            setTimeout(() => {
              this.resetForm();
              this.close();
            }, 100);
          }
        });
    }
  }

  private resetForm(): void {
    this.parcelForm.reset({
      parcelWeight: null,
      parcelVolume: null,
      parcelLength: null,
      parcelWidth: null,
      parcelHeight: null
    });
    this.isSubmitting = false;
    this.showVolumeAlert = false;
    this.calculatedVolume = 0;
  }

  getFieldError(fieldName: string): string {
    const field = this.parcelForm.get(fieldName);
    if (field?.errors && field.touched) {
      if (field.errors['required']) {
        return 'Questo campo è obbligatorio';
      }
      if (field.errors['min']) {
        if (fieldName === 'parcelWeight') {
          return 'Il peso non può essere negativo';
        }
        return 'Il valore deve essere maggiore di 0';
      }
    }
    return '';
  }

  getParcelFieldError(fieldName: string): string {
    return this.getFieldError(fieldName);
  }
}
